/**
 * Product page scroll motion, ported from the "BPN PDP (Whey) | Woolly-style
 * Motion" prototype (GSAP + ScrollTrigger). Loaded only on product templates
 * — see layout/theme.liquid. Sections whose "Enable animations" setting is off
 * are skipped (animated() in assets/component-motion.js).
 *
 * Every animation starts from a state GSAP sets at init, so with GSAP blocked or
 * reduced motion requested the page renders exactly as built. The primitives
 * are shared with the homepage and collection page in assets/component-motion.js.
 * Each part checks its section exists, so templates without a section skip it.
 *
 * Prototype → theme section
 *   Hero: gallery + buy panel      → pdp-breadcrumb, product
 *   Trust strip                    → trust-bar
 *   Transparency accordion         → product-accordions
 *   Features (icon cards)          → pdp-detail-cards
 *   Ingredients / media carousel   → pdp-media-carousel
 *   Ingredients + compare table    → ingredient-transparency
 *   Features ("Protein you can trust") → product-benefits
 *   UGC                            → video-slider
 *   Hotspot gallery (apparel)      → feature-gallery
 *   Triptych (bundle collections)  → collection-highlights
 *   Complete your stack            → complete-your-stack
 *   FAQ / "Why we built"           → Faq, faq-2
 *   The science (image-with-text)  → quiz-banner
 *   Reviews                        → pdp-review (Okendo renders the widget
 *                                    itself, so it rises as one block)
 *   Footer                         → footer
 */

import {
  EASE,
  animateFooter,
  animated,
  motionOff,
  promoteScrubTargets,
  revealWords,
  rise,
  setupMotion,
  splitWords,
  wipe,
} from './component-motion.js'; // eslint-disable-line import/extensions

const { gsap, ScrollTrigger } = window;

const PENDING_CLASS = 'motion-pending';

/* GSAP's back.out(2.4) as a CSS timing function, for pops run by popIn(). */
const BACK_OUT = 'cubic-bezier(0.34, 1.8, 0.64, 1)';

/**
 * Scale-and-fade pop that fires once, run with the Web Animations API on the
 * individual `scale` and `opacity` properties. For elements positioned with a
 * CSS `translate` (the gallery hotspots): a GSAP scale writes `transform` and
 * folds that `translate` into it, which breaks the centring.
 */
function popIn(elements, { trigger, start = 'top 82%', delay = 0, stagger = 0, duration = 0.55 } = {}) {
  const targets = [...elements];
  if (!targets.length) return;

  targets.forEach((element) => {
    element.style.opacity = '0';
  });
  ScrollTrigger.create({
    trigger: trigger || targets[0],
    start,
    once: true,
    onEnter: () =>
      targets.forEach((element, index) => {
        element.style.opacity = '';
        element.animate([{ scale: 0, opacity: 0 }, { scale: 1, opacity: 1 }], {
          duration: duration * 1000,
          delay: (delay + index * stagger) * 1000,
          easing: BACK_OUT,
          fill: 'backwards',
        });
      }),
  });
}

if (!customElements.get('product-motion')) {
  class ProductMotion extends HTMLElement {
    context = null;
    abortController = null;
    stackObservers = [];

    connectedCallback() {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (reducedMotion || !gsap || !ScrollTrigger) {
        this.releasePending();
        return;
      }

      setupMotion();
      this.abortController = new AbortController();
      this.context = gsap.context(() => {
        this.init();
        promoteScrubTargets();
      });
      this.releasePending();

      const { signal } = this.abortController;
      const refresh = () => ScrollTrigger.refresh();
      window.addEventListener('load', refresh, { once: true, signal });
      document.addEventListener('shopify:section:load', refresh, { signal });
      document.addEventListener('product:variant-change', () => this.onVariantChange(), { signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.stackObservers.forEach((observer) => observer.disconnect());
      this.context?.revert();
    }

    releasePending() {
      document.documentElement.classList.remove(PENDING_CLASS);
    }

    init() {
      this.animateHero();
      this.animateBuyPanel();
      this.animateTrustBar();
      this.animateAccordions();
      this.animateDetailCards();
      this.animateMediaCarousel();
      this.animateIngredients();
      this.animateBenefits();
      this.animateVideos();
      this.animateFeatureGallery();
      this.animateHighlights();
      this.animateStack();
      this.animateFaq();
      this.animateBanner();
      this.animateReviews();
      animateFooter();
    }

    /* Hero, run on load: the main image wipes down and settles from a zoom,
       thumbnails and breadcrumb lift, the title rises word by word, then the
       price, copy, flavours and purchase options follow in sequence. While the
       sticky gallery holds, the image zooms slightly as the details scroll. */
    animateHero() {
      const info = document.querySelector('product-info');
      if (!info || motionOff(info)) {
        rise(animated('.pdp-breadcrumb__list > li'), { y: 12, duration: 0.6, stagger: 0.04 });
        return;
      }

      const timeline = gsap.timeline({ defaults: { ease: 'power3.out' }, delay: 0.05 });
      const stage = info.querySelector('.product-media-gallery__main');

      if (stage) {
        gsap.set(stage, { clipPath: 'inset(0% 0% 100% 0%)' });
        timeline.to(stage, { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.1 }, 0);

        const image = stage.querySelector('.swiper-slide-active img') || stage.querySelector('.swiper-slide img');
        if (image) {
          gsap.set(image, { scale: 1.12 });
          timeline.to(image, { scale: 1, duration: 1.8, ease: EASE, clearProps: 'transform' }, 0);
        }
      }

      const fromTo = (targets, from, to, position) => {
        const elements = gsap.utils.toArray(targets).filter(Boolean);
        if (elements.length) timeline.fromTo(elements, from, to, position);
      };

      fromTo(info.querySelector('.product-media-gallery__badge'), { scale: 0.5, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(2.4)' }, 0.7);
      fromTo(
        info.querySelectorAll('.product-media-gallery__thumbnails .swiper-slide'),
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7, stagger: 0.07, clearProps: 'transform' },
        0.2
      );
      fromTo(animated('.pdp-breadcrumb__list > li'), { y: 12, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, stagger: 0.04 }, 0);
      fromTo(info.querySelector('.PDP__reviews'), { y: 16, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7 }, 0.1);

      const title = info.querySelector('.product__title h1');
      if (title) {
        const words = splitWords(title);
        gsap.set(words, { yPercent: 110 });
        timeline.to(words, { yPercent: 0, duration: 1.1, stagger: 0.1 }, 0.15);
      }

      fromTo(
        info.querySelectorAll('.product__intro-line, .product__price-group, .product__short-description'),
        { y: 16, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, stagger: 0.08 },
        0.4
      );
      fromTo(info.querySelector('.variant-select__images-header'), { y: 16, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6 }, 0.6);
      fromTo(
        info.querySelectorAll('.variant-select__image-item'),
        { y: 22, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, stagger: 0.045, clearProps: 'transform' },
        0.65
      );
      fromTo(info.querySelector('.product__subscription'), { y: 24, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7, clearProps: 'transform' }, 0.85);

      /* The zoom is on each slide's .product-media, not the <img>, so it never
         fights the intro's zoom on the image. It is driven through one custom
         property on the gallery (read in assets/component-product-motion.css)
         rather than a tween per slide: one style write per scroll frame however
         many slides the product has. */
      const gallery = info.querySelector('.product-media-gallery__main');
      const product = info.querySelector('.product');
      if (gallery && product) {
        const zoom = { scale: 1 };
        gsap.to(zoom, {
          scale: 1.04,
          ease: 'none',
          scrollTrigger: { trigger: product, start: 'top top', end: 'bottom top', scrub: true },
          onUpdate: () => gallery.style.setProperty('--product-media-zoom', zoom.scale.toFixed(4)),
        });
      }
    }

    /* The rest of the buy panel rises as it scrolls in: add to cart, the
       free-gift meter (its fill draws in and the stops pop), and Frequently
       bought together. */
    animateBuyPanel() {
      const info = document.querySelector('product-info');
      if (!info || motionOff(info)) return;

      const buttons = info.querySelector('.product__buy-buttons-group');
      rise([buttons, info.querySelector('.product__lightlabs')], { stagger: 0.1, trigger: buttons, start: 'top 92%' });

      const gift = info.querySelector('.gift-threshold-bar');
      if (gift) {
        const trigger = { trigger: gift, start: 'top 90%', once: true };
        rise(gift, { trigger: gift, start: 'top 92%' });

        const progress = gift.querySelector('.gift-threshold-bar__progress');
        if (progress) {
          gsap.fromTo(
            progress,
            { scaleX: 0, transformOrigin: '0% 50%' },
            { scaleX: 1, duration: 1.2, ease: 'power3.out', clearProps: 'transform', scrollTrigger: trigger }
          );
        }

        const stops = gift.querySelectorAll('.gift-threshold-bar__stop');
        if (stops.length) {
          gsap.fromTo(
            stops,
            { scale: 0 },
            { scale: 1, duration: 0.5, stagger: 0.1, delay: 0.5, ease: 'back.out(2.4)', clearProps: 'transform', scrollTrigger: trigger }
          );
        }
      }

      const fbt = info.querySelector('frequently-bought-together');
      if (fbt) {
        rise(fbt, { trigger: fbt, start: 'top 92%' });
        rise(fbt.querySelectorAll('.fbt__item, .fbt-bundle__row, .fbt__footer'), { y: 14, stagger: 0.08, delay: 0.2, trigger: fbt, start: 'top 92%' });
      }
    }

    /* Trust strip: labels lift, the dividers grow in. */
    animateTrustBar() {
      for (const bar of animated('.trust-bar')) {
        /* The marquee repeats its list several times over; only the copies on
           screen are revealed — the rest scroll in long after the reveal. */
        const onScreen = (element) => element.getBoundingClientRect().left < window.innerWidth;
        rise([...bar.querySelectorAll('.trust-bar__item')].filter(onScreen), { y: 16, stagger: 0.1, trigger: bar });

        const dividers = [...bar.querySelectorAll('.trust-bar__divider')].filter(onScreen);
        if (dividers.length) {
          gsap.fromTo(
            dividers,
            { scaleY: 0 },
            { scaleY: 1, duration: 0.6, stagger: 0.1, delay: 0.3, ease: 'back.out(2)', scrollTrigger: { trigger: bar, start: 'top 92%', once: true } }
          );
        }
      }
    }

    /* Transparency accordion: the heading rises word by word, the rows lift
       in, and an open row's nutrition panel wipes down. */
    animateAccordions() {
      for (const section of animated('.product-accordions')) {
        revealWords(section.querySelector('.product-accordions__heading'));
        rise(section.querySelectorAll('.product-accordions__intro-copy'), { trigger: section.querySelector('.product-accordions__intro') });

        const stack = section.querySelector('.product-accordions__stack');
        if (!stack) continue;

        rise(stack.querySelectorAll('.product-accordions__row'), { stagger: 0.1, trigger: stack, start: 'top 85%' });
        wipe(stack.querySelector('.product-accordions__row[open] .product-accordions__nutrition'), { trigger: stack, delay: 0.25 });
      }
    }

    /* Feature cards: heading word by word, cards lift, icons pop. */
    animateDetailCards() {
      for (const section of animated('.pdp-detail-cards')) {
        revealWords(section.querySelector('.pdp-detail-cards__heading'));
        rise(section.querySelector('.pdp-detail-cards__subheading'), { y: 16, duration: 0.6 });

        const swiper = section.querySelector('.pdp-detail-cards__swiper') || section;
        const trigger = { trigger: swiper, start: 'top 85%', once: true };
        rise(section.querySelectorAll('.pdp-detail-cards__card'), { y: 28, stagger: 0.12, trigger: swiper, start: 'top 85%' });

        const icons = section.querySelectorAll('.pdp-detail-cards__media');
        if (icons.length) {
          gsap.fromTo(
            icons,
            { scale: 0.4, rotate: -14, opacity: 0 },
            { scale: 1, rotate: 0, opacity: 1, duration: 0.7, ease: 'back.out(2)', stagger: 0.12, delay: 0.25, clearProps: 'transform', scrollTrigger: trigger }
          );
        }
      }
    }

    /* Media carousel: the image frame wipes down and eases out of a zoom on
       scroll; the cards lift and their icons pop. */
    animateMediaCarousel() {
      for (const section of animated('.pdp-media-carousel')) {
        const frame = section.querySelector('.pdp-media-carousel__frame');
        if (frame) {
          wipe(frame, { trigger: section, start: 'top 80%', duration: 1.2 });

          const image = frame.querySelector('.pdp-media-carousel__image, img');
          if (image) {
            gsap.fromTo(
              image,
              { scale: 1.14 },
              { scale: 1, ease: 'none', scrollTrigger: { trigger: section, start: 'top bottom', end: 'bottom top', scrub: true } }
            );
          }
        }

        const cards = section.querySelector('.pdp-media-carousel__cards');
        if (cards) {
          rise(cards.querySelectorAll('.pdp-media-carousel__card'), { stagger: 0.1, trigger: cards, start: 'top 85%' });

          const icons = cards.querySelectorAll('.pdp-media-carousel__icon');
          if (icons.length) {
            gsap.fromTo(
              icons,
              { scale: 0.4, rotate: -14, opacity: 0 },
              { scale: 1, rotate: 0, opacity: 1, duration: 0.7, ease: 'back.out(2)', stagger: 0.1, delay: 0.25, clearProps: 'transform', scrollTrigger: { trigger: cards, start: 'top 85%', once: true } }
            );
          }
        }
      }
    }

    /* Ingredient cards and the "How BPN compares" table: heading word by word,
       the intro lifts, cards rise and their dose pills pop; the table heading
       and rows rise, then each row's ticks and crosses pop in turn. */
    animateIngredients() {
      for (const section of animated('.ingredient-transparency')) {
        revealWords(section.querySelector('.ingredient-transparency__heading'));
        rise(section.querySelector('.ingredient-transparency__intro'), { trigger: section.querySelector('.ingredient-transparency__header') });

        const cards = section.querySelector('.ingredient-transparency__cards');
        if (cards) {
          const trigger = { trigger: cards, start: 'top 85%' };
          rise(cards.querySelectorAll('.ingredient-transparency__card'), { ...trigger, stagger: 0.1 });

          const pills = cards.querySelectorAll('.ingredient-transparency__card-dose');
          if (pills.length) {
            gsap.fromTo(
              pills,
              { scale: 0.5, opacity: 0 },
              { scale: 1, opacity: 1, duration: 0.55, ease: 'back.out(2.4)', stagger: 0.1, delay: 0.45, clearProps: 'transform', scrollTrigger: { ...trigger, once: true } }
            );
          }
        }

        const compare = section.querySelector('.ingredient-transparency__compare');
        if (!compare) continue;

        rise(compare.querySelector('.ingredient-transparency__compare-heading'), { y: 16 });
        const table = compare.querySelector('.ingredient-transparency__table') || compare;
        rise(table.querySelectorAll('tr'), { y: 18, stagger: 0.07, duration: 0.7, trigger: table, start: 'top 85%' });

        table.querySelectorAll('.ingredient-transparency__row').forEach((row, index) => {
          const marks = row.querySelectorAll('.ingredient-transparency__mark');
          if (!marks.length) return;

          gsap.fromTo(
            marks,
            { scale: 0.4, rotate: -14, opacity: 0 },
            { scale: 1, rotate: 0, opacity: 1, duration: 0.7, ease: 'back.out(2)', stagger: 0.06, delay: 0.2 + index * 0.11, clearProps: 'transform', scrollTrigger: { trigger: table, start: 'top 80%', once: true } }
          );
        });
      }
    }

    /* "Protein you can trust": heading word by word, cards lift, icons pop. */
    animateBenefits() {
      for (const section of animated('.product-benefits')) {
        revealWords(section.querySelector('.product-benefits__heading'));

        const grid = section.querySelector('.product-benefits__grid');
        if (!grid) continue;

        rise(grid.querySelectorAll('.product-benefits__card'), { y: 28, stagger: 0.12, trigger: grid, start: 'top 85%' });

        const icons = grid.querySelectorAll('.product-benefits__icon-box');
        if (icons.length) {
          gsap.fromTo(
            icons,
            { scale: 0.4, rotate: -14, opacity: 0 },
            { scale: 1, rotate: 0, opacity: 1, duration: 0.7, ease: 'back.out(2)', stagger: 0.12, delay: 0.25, clearProps: 'transform', scrollTrigger: { trigger: grid, start: 'top 85%', once: true } }
          );
        }
      }
    }

    /* UGC: heading word by word, the intro copy and badge lift, video cards
       lift in, the footer row follows. */
    animateVideos() {
      for (const section of animated('.video-slider')) {
        revealWords(section.querySelector('.video-slider__heading .heading-tag'));
        rise(section.querySelectorAll('.video-slider__header-content, .video-slider__header-badge'), { y: 16, stagger: 0.1 });

        const slider = section.querySelector('.video-slider__slider') || section;
        rise(section.querySelectorAll('.video-slider__slide'), { stagger: 0.05, duration: 0.7, trigger: slider, start: 'top 82%' });

        const footer = section.querySelector('.video-slider__footer');
        if (footer) rise(footer.children, { y: 16, stagger: 0.1, trigger: footer });
      }
    }

    /* Complete your stack: heading word by word; each card's image wipes down
       and settles from a zoom in turn, its badge pops and its details lift. */
    animateStack() {
      for (const section of animated('.complete-your-stack')) {
        /* Without a cross_sells list the section shows Shopify's recommendations,
           which <product-recommendations> fetches when it nears the viewport and
           writes into itself — after this has run. Wait for that content. */
        if (!section.querySelector('.complete-your-stack__grid') && section.querySelector('product-recommendations')) {
          this.whenStackLoads(section);
          continue;
        }

        this.animateStackSection(section);
      }
    }

    whenStackLoads(section) {
      const observer = new MutationObserver(() => {
        if (!section.querySelector('.complete-your-stack__grid')) return;

        observer.disconnect();
        this.context.add(() => this.animateStackSection(section));
        // The section just grew from empty to full height; every trigger below it moved.
        ScrollTrigger.refresh();
      });
      observer.observe(section, { childList: true, subtree: true });
      this.stackObservers.push(observer);
    }

    animateStackSection(section) {
      revealWords(section.querySelector('.complete-your-stack__heading'));
      rise(section.querySelector('.complete-your-stack__subheading'), { trigger: section.querySelector('.complete-your-stack__header') });

      const grid = section.querySelector('.complete-your-stack__grid');
      if (!grid) return;

      const trigger = { trigger: grid, start: 'top 85%', once: true };
      grid.querySelectorAll('.complete-your-stack__card').forEach((card, index) => {
        const offset = index * 0.12;
        wipe(card.querySelector('.complete-your-stack__media'), { trigger: grid, delay: offset });

        const image = card.querySelector('.complete-your-stack__media img');
        if (image) {
          gsap.fromTo(image, { scale: 1.12 }, { scale: 1, duration: 1.4, ease: EASE, delay: offset, clearProps: 'transform', scrollTrigger: trigger });
        }

        const badge = card.querySelector('.complete-your-stack__badge');
        if (badge) {
          gsap.fromTo(badge, { scale: 0.5, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(2.4)', delay: 0.7 + offset, scrollTrigger: trigger });
        }

        const info = card.querySelector('.complete-your-stack__info');
        rise([...(info?.children || []), card.querySelector('.complete-your-stack__button--card')], {
          y: 18,
          stagger: 0.06,
          delay: 0.3 + offset,
          trigger: grid,
          start: 'top 85%',
        });
      });

      const actions = section.querySelector('.complete-your-stack__actions');
      if (actions) rise(actions.querySelector('.complete-your-stack__button--add-all'), { y: 16, trigger: actions });
    }

    /* Hotspot gallery (apparel): heading word by word; the photos wipe down in
       turn and ease out of a zoom on scroll; the hotspots pop; the spec cards
       lift and their icons pop. */
    animateFeatureGallery() {
      for (const section of animated('.feature-gallery')) {
        revealWords(section.querySelector('.feature-gallery__heading'));

        const gallery = section.querySelector('.feature-gallery__gallery');
        if (gallery) {
          const slides = gallery.querySelectorAll('.feature-gallery__slide');
          // clip-path is cleared once each wipe ends: the slides are overflow:
          // visible on desktop so the hotspot tooltips can sit outside them.
          slides.forEach((slide, index) => {
            gsap.fromTo(
              slide,
              { clipPath: 'inset(0% 0% 100% 0%)' },
              { clipPath: 'inset(0% 0% 0% 0%)', duration: 1.1, ease: 'power3.out', delay: index * 0.1, clearProps: 'clipPath', scrollTrigger: { trigger: gallery, start: 'top 82%', once: true } }
            );
          });

          /* One custom property drives every photo's zoom (read in
             assets/component-product-motion.css), which also clips the zoom to
             the photo's own box since the slide no longer does. */
          if (slides.length) {
            const zoom = { scale: 1.14 };
            gsap.to(zoom, {
              scale: 1,
              ease: 'none',
              scrollTrigger: { trigger: section, start: 'top bottom', end: 'bottom top', scrub: true },
              onUpdate: () => section.style.setProperty('--feature-gallery-zoom', zoom.scale.toFixed(4)),
            });
          }

          popIn(gallery.querySelectorAll('.feature-gallery__hotspot'), { trigger: gallery, delay: 0.8, stagger: 0.12 });
        }

        const cards = section.querySelector('.feature-gallery__cards');
        if (!cards) continue;

        rise(section.querySelector('.feature-gallery__specs-heading'), { y: 16, trigger: cards, start: 'top 90%' });
        rise(cards.querySelectorAll('.feature-gallery__card'), { y: 28, stagger: 0.1, trigger: cards, start: 'top 90%' });

        const icons = cards.querySelectorAll('.feature-gallery__icon');
        if (icons.length) {
          gsap.fromTo(
            icons,
            { scale: 0.4, rotate: -14, opacity: 0 },
            { scale: 1, rotate: 0, opacity: 1, duration: 0.7, ease: 'back.out(2)', stagger: 0.1, delay: 0.25, clearProps: 'transform', scrollTrigger: { trigger: cards, start: 'top 90%', once: true } }
          );
        }
      }
    }

    /* Collection highlights (bundle triptych): each panel's image wipes down
       in turn and drifts on scroll; its heading rises word by word, then the
       unit label, copy and button. */
    animateHighlights() {
      for (const section of animated('.collection-highlights__row')) {
        section.querySelectorAll('.collection-highlights__card').forEach((card, index) => {
          const offset = index * 0.12;
          const trigger = { trigger: section, start: 'top 80%' };
          wipe(card.querySelector('.collection-highlights__media'), { ...trigger, delay: offset });

          const image = card.querySelector('.collection-highlights__image');
          if (image) {
            gsap.fromTo(
              image,
              { scale: 1.12, yPercent: -6 },
              { scale: 1.12, yPercent: 6, ease: 'none', scrollTrigger: { trigger: card, start: 'top bottom', end: 'bottom top', scrub: true } }
            );
          }

          revealWords(card.querySelector('.collection-highlights__heading'), { ...trigger, delay: 0.35 + offset });
          rise(card.querySelectorAll('.collection-highlights__eyebrow, .collection-highlights__text, .collection-highlights__cta'), {
            ...trigger,
            y: 18,
            stagger: 0.08,
            delay: 0.45 + offset,
          });
        });
      }
    }

    /* FAQ: heading word by word, the description and each question lift. */
    animateFaq() {
      for (const section of animated('.faq-section')) {
        /* "Why we built" (faq-2): the image wipes down and eases out of a zoom on scroll. */
        for (const media of section.querySelectorAll('.faq-section__media')) {
          wipe(media, { trigger: section, start: 'top 80%', duration: 1.2 });

          const image = media.querySelector('.faq-section__image');
          if (image) {
            gsap.fromTo(
              image,
              { scale: 1.14, yPercent: -4 },
              { scale: 1, yPercent: 0, ease: 'none', scrollTrigger: { trigger: section, start: 'top bottom', end: 'bottom top', scrub: true } }
            );
          }
        }

        revealWords(section.querySelector('.faq-section__heading'));
        rise(section.querySelector('.faq-section__description'), { trigger: section.querySelector('.faq-section__intro') });

        const accordion = section.querySelector('.faq-section__accordion');
        if (accordion) rise(accordion.querySelectorAll('.faq-section__item'), { y: 20, stagger: 0.08, trigger: accordion });
      }
    }

    /* Image-with-text banner ("The science" in the prototype): the media wipes
       down and eases out of a zoom on scroll, the heading rises word by word,
       the copy and button follow. */
    animateBanner() {
      for (const banner of animated('.quiz-banner')) {
        const trigger = { trigger: banner, start: 'top 80%' };
        const media = banner.querySelector('.quiz-banner__media');
        wipe(media, { ...trigger, duration: 1.2 });

        const image = banner.querySelector('.quiz-banner__image, .quiz-banner__media img');
        if (image) {
          gsap.fromTo(
            image,
            { scale: 1.14, yPercent: -4 },
            { scale: 1, yPercent: 0, ease: 'none', scrollTrigger: { trigger: banner, start: 'top bottom', end: 'bottom top', scrub: true } }
          );
        }

        revealWords(banner.querySelector('.quiz-banner__heading'), { trigger: banner, start: 'top 85%' });
        rise(banner.querySelectorAll('.quiz-banner__eyebrow, .quiz-banner__body'), { ...trigger, stagger: 0.1 });
        rise(banner.querySelector('.quiz-banner__button'), { ...trigger, y: 16, duration: 0.6, delay: 0.35 });
      }
    }

    /* Reviews: the title rises word by word and the Okendo widget lifts in as
       one block — its contents are rendered by the app after load. */
    animateReviews() {
      for (const section of animated('.pdp-reviews-section')) {
        revealWords(section.querySelector('.sectionTitle'));
        rise(section.querySelector('.okeReviews'), { y: 28, trigger: section, start: 'top 85%' });
      }
    }

    /* A flavour change re-renders the picker: the flavour name pops back in
       and the new image settles. */
    onVariantChange() {
      const info = document.querySelector('product-info');
      if (!info || motionOff(info)) return;

      this.context?.add(() => {
        const current = info.querySelector('.variant-select__images-current');
        if (current) gsap.fromTo(current, { yPercent: 60, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.45, ease: 'back.out(2)' });

        const image = info.querySelector('.product-media-gallery__main .swiper-slide-active img');
        if (image) gsap.fromTo(image, { scale: 1.06, rotate: -2 }, { scale: 1, rotate: 0, duration: 0.9, ease: 'back.out(1.6)', clearProps: 'transform' });
      });
    }
  }

  customElements.define('product-motion', ProductMotion);
}
